import { Component, OnInit, Optional } from '@angular/core';
import { UsersService } from 'ldt-identity-service-api';
import { AuthService } from '../auth/service/auth.service';
import { NotificationService } from '../shared/notification-service/notification.service';
import { WorkforceAppComponent } from '../workforce/app.wf.component';

@Component({
  selector: 'app-account-management',
  templateUrl: './account-management.component.html',
  styleUrls: ['./account-management.component.scss'],
  standalone: false,
})
export class AccountManagementComponent implements OnInit {
  name: string = '';
  email: string = '';
  savingName: boolean = false;
  sendingReset: boolean = false;
  resetEmailSent: boolean = false;
  private userId: string = '';

  constructor(
    private authService: AuthService,
    private usersService: UsersService,
    private notify: NotificationService,
    // Present only when rendered inside the Workforce shell, which (unlike
    // the GLD shell) provides no content padding of its own.
    @Optional() public wfParent: WorkforceAppComponent | null
  ) {}

  ngOnInit() {
    const user = this.authService.getUserValue;
    if (user) {
      this.userId = user.id;
      this.email = user.email || '';
      this.name = user.name || '';
    }
  }

  // Commit the inline edit on Enter instead of inserting a newline.
  onNameKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
  }

  saveName(el: EventTarget | null) {
    const target = el as HTMLElement;
    const newName = (target?.textContent || '').trim();
    if (!newName || newName === this.name) {
      // Revert empty or unchanged edits
      if (target) {
        target.textContent = this.name;
      }
      return;
    }

    this.savingName = true;
    this.usersService.updateUser(this.userId, { name: newName }).subscribe({
      next: () => {
        this.name = newName;
        this.savingName = false;
        this.notify.success('Name updated');
        // Refresh the session so the updated name propagates immediately
        // (e.g. the rail's avatar initials) instead of on next login.
        this.authService.refreshSession().subscribe({ error: () => {} });
      },
      error: () => {
        if (target) {
          target.textContent = this.name;
        }
        this.savingName = false;
        this.notify.error('Failed to update your name. Please try again later.');
      },
    });
  }

  // There is no change-password endpoint; password changes go through the
  // email reset flow, initiated here with the signed-in user's email.
  sendPasswordReset() {
    if (!this.email || this.sendingReset || this.resetEmailSent) {
      return;
    }
    this.sendingReset = true;
    this.usersService.initiatePasswordReset({ email: this.email }).subscribe({
      next: () => {
        this.sendingReset = false;
        this.resetEmailSent = true;
      },
      error: () => {
        this.sendingReset = false;
        this.notify.error('Failed to send the reset email. Please try again later.');
      },
    });
  }
}
